import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../supabase/supabaseClient'
import { convertOdds } from '../utils/oddsConverter'
import { BOOKMAKER_LOGOS, BOOKMAKERS_BY_REGION as BOOKMAKERS } from '../config/bookmakers'
import { ALL_SPORTS as SPORTS } from '../config/sports'
import { saveUserFilter, getUserFilters, deleteUserFilter, setDefaultFilter, unsetDefaultFilter, getDefaultFilter } from '../services/userDataService'
import FilterPanel from './FilterPanel'
import PreviewWall from '../components/PreviewWall'
import HiddenBetsModal from '../ARB_NEW_PAGE/HiddenBetsModal'
import TrackBetModal, { useBonusTrack } from './TrackBetModal'
import BonusCard, { formatSelection } from './BonusCard'
import BonusTour from './bonusTour'
import '../styles/app.css'
import '../styles/app-mobile.css'
import '../ARB_NEW_PAGE/ArbNew.css'
import './BonusNew.css'
import useVisiblePolling from '../hooks/useVisiblePolling'

const getHoursUntil = (ct) => Math.max(0, (new Date(ct + 'Z') - new Date()) / 3600000)

export default function BonusNewMobile({ isVisible = true, isPreview: isPreviewProp = false }) {
  const { user, oddsFormat, hasAccess, hadTrial, loading: authLoading } = useAuth()
  const isPreview = isPreviewProp || authLoading || !hasAccess('bonus')

  // ─── Data ─────────────────────────────────────────────────────────────────
  const [opportunities, setOpportunities]           = useState([])
  const [loading, setLoading]                       = useState(true)
  const [error, setError]                           = useState(null)
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false)

  // ─── Bonus amount ─────────────────────────────────────────────────────────
  const [defaultBonusAmount, setDefaultBonusAmount] = useState(50)

  // ─── Filters ──────────────────────────────────────────────────────────────
  const [filters, setFilters] = useState({
    sports:          new Set(),
    bonusBookmakers: new Set(),
    hedgeBookmakers: new Set(),
    odds:            [1, 6],
    retention:       [65, 100],
    hoursUntil:      [0, 72],
  })
  const [savedFilters, setSavedFilters]               = useState([])
  const [filterName, setFilterName]                   = useState('')
  const [activeFilterId, setActiveFilterId]           = useState(null)
  const [defaultFilterLoaded, setDefaultFilterLoaded] = useState(false)
  const [showFilterPanel, setShowFilterPanel]         = useState(false)

  // ─── Sort + pagination ────────────────────────────────────────────────────
  const [sortBy, setSortBy]           = useState('retention_percentage')
  const [sortDir, setSortDir]         = useState('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 25

  // ─── Card state ───────────────────────────────────────────────────────────
  const [expandedCard, setExpandedCard] = useState(null)
  const [editableData, setEditableData] = useState({})

  // ─── Hidden bets ──────────────────────────────────────────────────────────
  const [hiddenBets, setHiddenBets]           = useState({})
  const [showHiddenModal, setShowHiddenModal] = useState(false)

  // ─── Fetch ────────────────────────────────────────────────────────────────
  const fetchOpportunities = async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      const { data, error } = await supabase.functions.invoke('bonus-proxy')
      if (error) throw error
      setOpportunities(data.opportunities || [])
      setError(null)
    } catch (err) {
      setError(err.message)
    } finally {
      if (!silent) setLoading(false)
    }
  }

  useEffect(() => { fetchOpportunities() }, [])

  useVisiblePolling(() => fetchOpportunities(true), 30000, autoRefreshEnabled && isVisible)

  // ─── Hidden bets bootstrap ────────────────────────────────────────────────
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('hidden-bonus-bets') || '{}')
    const now = new Date()
    const cleaned = {}
    Object.entries(stored).forEach(([id, data]) => {
      const gameTime = new Date(data.commence_time + 'Z')
      if (now < new Date(gameTime.getTime() + 3600000)) cleaned[id] = data
    })
    localStorage.setItem('hidden-bonus-bets', JSON.stringify(cleaned))
    setHiddenBets(cleaned)
  }, [])

  // ─── Filter persistence ───────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.id) {
      setFilters(prev => ({ ...prev, sports: new Set(SPORTS), bonusBookmakers: new Set(BOOKMAKERS.AU), hedgeBookmakers: new Set(BOOKMAKERS.AU) }))
      return
    }
    getUserFilters(user.id, 'bonus').then(({ data }) => {
      if (data) setSavedFilters(data.map(f => ({ id: f.id, name: f.filter_name, isDefault: f.is_default || false, ...f.filter_data })))
    })
  }, [user?.id])

  useEffect(() => {
    if (!user?.id || defaultFilterLoaded) return
    getDefaultFilter(user.id, 'bonus').then(({ data }) => {
      if (data?.filter_data) applyFilterData(data.filter_data)
      else setFilters(prev => ({ ...prev, sports: new Set(SPORTS), bonusBookmakers: new Set(BOOKMAKERS.AU), hedgeBookmakers: new Set(BOOKMAKERS.AU) }))
      setDefaultFilterLoaded(true)
    })
  }, [user?.id, defaultFilterLoaded])

  const applyFilterData = (fd) => {
    // Prefer the saved excludedSports blacklist (newer filters) so leagues added after the
    // filter was saved stay visible (selected = all minus excluded). Fall back to the legacy
    // sports whitelist for old filters.
    const selectedSports = Array.isArray(fd.filters.excludedSports)
      ? SPORTS.filter(s => !fd.filters.excludedSports.includes(s))
      : (fd.filters.sports || SPORTS)
    setFilters({
      sports:          new Set(selectedSports),
      bonusBookmakers: new Set(fd.filters.bonusBookmakers || BOOKMAKERS.AU),
      hedgeBookmakers: new Set(fd.filters.hedgeBookmakers || BOOKMAKERS.AU),
      odds:            fd.filters.odds       || [1, 6],
      retention:       fd.filters.retention  || [65, 100],
      hoursUntil:      fd.filters.hoursUntil || [0, 72],
    })
    if (fd.bonusSettings) setDefaultBonusAmount(fd.bonusSettings.defaultBonusAmount ?? 50)
  }

  const handleSaveFilter = async (name) => {
    if (!name.trim() || !user?.id) return
    const fd = {
      filters: {
        sports:          Array.from(filters.sports),                     // legacy whitelist (back-compat)
        excludedSports:  SPORTS.filter(s => !filters.sports.has(s)),     // blacklist = source of truth on load
        bonusBookmakers: Array.from(filters.bonusBookmakers),
        hedgeBookmakers: Array.from(filters.hedgeBookmakers),
        odds:            filters.odds,
        retention:       filters.retention,
        hoursUntil:      filters.hoursUntil,
      },
      bonusSettings: { defaultBonusAmount },
      createdAt: new Date().toISOString(),
    }
    const { data, error } = await saveUserFilter(user.id, 'bonus', name.trim(), fd)
    if (error) { alert('Error saving filter: ' + error); return }
    if (data) setSavedFilters(prev => [...prev, { id: data.id, name: data.filter_name, isDefault: false, ...data.filter_data }])
    setFilterName('')
  }

  const handleLoadFilter   = (saved) => { applyFilterData(saved); setActiveFilterId(saved.id) }
  const handleDeleteFilter = async (id) => {
    if (!user?.id) return
    const { error } = await deleteUserFilter(id)
    if (error) { alert('Error deleting filter: ' + error); return }
    setSavedFilters(prev => prev.filter(f => f.id !== id))
    if (activeFilterId === id) setActiveFilterId(null)
  }
  const handleToggleDefault = async (id) => {
    if (!user?.id) return
    const current = savedFilters.find(f => f.id === id)
    if (current?.isDefault) await unsetDefaultFilter(user.id, 'bonus', id)
    else await setDefaultFilter(user.id, 'bonus', id)
    setSavedFilters(prev => prev.map(f => ({ ...f, isDefault: f.id === id ? !current?.isDefault : false })))
  }
  const resetFilters = () => {
    setFilters({ sports: new Set(SPORTS), bonusBookmakers: new Set(BOOKMAKERS.AU), hedgeBookmakers: new Set(BOOKMAKERS.AU), odds: [1, 6], retention: [65, 100], hoursUntil: [0, 24] })
    setDefaultBonusAmount(50)
  }

  // ─── Bonus calculation ────────────────────────────────────────────────────
  const calculateBonusConversion = (outcomes, customBonusAmount = null) => {
    if (!outcomes || outcomes.length !== 2) return { bonusStake: 0, hedgeStake: 0, guaranteedProfit: 0, retentionPercentage: 0, bonusWinProfit: 0, hedgeWinProfit: 0 }
    const bonusOutcome = outcomes.find(o => o.bet_type === 'BONUS')
    const hedgeOutcome = outcomes.find(o => o.bet_type === 'HEDGE')
    if (!bonusOutcome || !hedgeOutcome) return { bonusStake: 0, hedgeStake: 0, guaranteedProfit: 0, retentionPercentage: 0, bonusWinProfit: 0, hedgeWinProfit: 0 }
    const bonusAmount    = customBonusAmount ?? defaultBonusAmount
    const bonusWinnings  = bonusAmount * (bonusOutcome.odds - 1)
    const hedgeStake     = bonusWinnings / hedgeOutcome.odds
    const bonusWinProfit = bonusWinnings - hedgeStake
    const hedgeWinProfit = hedgeStake * (hedgeOutcome.odds - 1)
    const guaranteedProfit    = Math.min(bonusWinProfit, hedgeWinProfit)
    const retentionPercentage = (guaranteedProfit / bonusAmount) * 100
    return {
      bonusStake:          bonusAmount,
      hedgeStake:          Math.round(hedgeStake * 100) / 100,
      guaranteedProfit:    Math.round(guaranteedProfit * 100) / 100,
      retentionPercentage: Math.round(retentionPercentage * 100) / 100,
      bonusWinProfit:      Math.round(bonusWinProfit * 100) / 100,
      hedgeWinProfit:      Math.round(hedgeWinProfit * 100) / 100,
    }
  }



  // ─── Editable data helpers ────────────────────────────────────────────────
  const getEditableOdds       = (id, idx, def) => editableData[id]?.odds?.[idx]          !== undefined ? editableData[id].odds[idx]          : def
  const getEditableOddsRaw    = (id, idx, def) => editableData[id]?.oddsRaw?.[idx]       !== undefined ? editableData[id].oddsRaw[idx]       : String(def)
  const getEditableBonusAmt   = (id)           => editableData[id]?.bonusAmount           !== undefined ? editableData[id].bonusAmount           : defaultBonusAmount
  const getEditableBonusRaw   = (id)           => editableData[id]?.bonusAmountRaw        !== undefined ? editableData[id].bonusAmountRaw        : String(defaultBonusAmount)

  const updateOdds = (id, idx, val) => setEditableData(prev => {
    const d = { ...(prev[id] || {}) }
    d.odds = [...(d.odds || [])]; d.oddsRaw = [...(d.oddsRaw || [])]
    d.oddsRaw[idx] = val
    const num = parseFloat(val)
    d.odds[idx] = isNaN(num) ? d.odds[idx] : num
    return { ...prev, [id]: d }
  })

  const updateBonusAmount = (id, val) => setEditableData(prev => {
    const num = parseFloat(val)
    return { ...prev, [id]: { ...(prev[id] || {}), bonusAmountRaw: val, bonusAmount: isNaN(num) ? (prev[id]?.bonusAmount ?? defaultBonusAmount) : num } }
  })

  // ─── Hidden bets ──────────────────────────────────────────────────────────
  const generateHideId = (opp) => {
    const s1 = opp.outcomes?.[0]?.selection || '', s2 = opp.outcomes?.[1]?.selection || ''
    const b1 = opp.outcomes?.[0]?.bookmaker || '', b2 = opp.outcomes?.[1]?.bookmaker || ''
    return `${opp.sport}_${opp.match}_${opp.market_type}_${s1}_${s2}_${b1}_${b2}_${opp.commence_time}`.replace(/[^a-zA-Z0-9_]/g, '_')
  }

  const hideOpportunity = (opp) => {
    const id = generateHideId(opp)
    const updated = { ...hiddenBets, [id]: { match: opp.match, selection1: opp.outcomes?.[0]?.selection, selection2: opp.outcomes?.[1]?.selection, bookmaker1: opp.outcomes?.[0]?.bookmaker, bookmaker2: opp.outcomes?.[1]?.bookmaker, commence_time: opp.commence_time, sport: opp.sport, market_type: opp.market_type } }
    localStorage.setItem('hidden-bonus-bets', JSON.stringify(updated))
    setHiddenBets(updated)
  }

  const unhideOpportunity = (betId) => {
    const updated = { ...hiddenBets }
    delete updated[betId]
    localStorage.setItem('hidden-bonus-bets', JSON.stringify(updated))
    setHiddenBets(updated)
  }

  const clearAllHidden = () => { localStorage.removeItem('hidden-bonus-bets'); setHiddenBets({}) }

  // ─── Track ───────────────────────────────────────────────────────────────
  const track = useBonusTrack({ hideOpportunity })

  // ─── Filter + sort ────────────────────────────────────────────────────────
  const filteredOpportunities = useMemo(() => {
    const filtered = opportunities.filter(opp => {
      if (filters.sports.size > 0 && !filters.sports.has(opp.sport)) return false
      const bonusOutcome = opp.outcomes?.find(o => o.bet_type === 'BONUS')
      const hedgeOutcome = opp.outcomes?.find(o => o.bet_type === 'HEDGE')
      if (bonusOutcome && filters.bonusBookmakers.size > 0 && !filters.bonusBookmakers.has(bonusOutcome.bookmaker)) return false
      if (hedgeOutcome && filters.hedgeBookmakers.size > 0 && !filters.hedgeBookmakers.has(hedgeOutcome.bookmaker)) return false
      if (opp.outcomes?.some(o => o.odds < filters.odds[0] || o.odds > filters.odds[1])) return false
      const retention = calculateBonusConversion(opp.outcomes, defaultBonusAmount).retentionPercentage
      if (retention < filters.retention[0] || retention > filters.retention[1]) return false
      const hrs = getHoursUntil(opp.commence_time)
      if (hrs < filters.hoursUntil[0] || hrs > filters.hoursUntil[1]) return false
      return true
    })
    return filtered.sort((a, b) => {
      let av, bv
      if (sortBy === 'retention_percentage') {
        av = calculateBonusConversion(a.outcomes, defaultBonusAmount).retentionPercentage
        bv = calculateBonusConversion(b.outcomes, defaultBonusAmount).retentionPercentage
      } else if (sortBy === 'odds') {
        av = a.outcomes?.reduce((s, o) => s + o.odds, 0) / (a.outcomes?.length || 1)
        bv = b.outcomes?.reduce((s, o) => s + o.odds, 0) / (b.outcomes?.length || 1)
      } else if (sortBy === 'start_time') {
        av = new Date(a.commence_time).getTime()
        bv = new Date(b.commence_time).getTime()
      } else if (sortBy === 'sport') {
        return sortDir === 'desc' ? b.sport.localeCompare(a.sport) : a.sport.localeCompare(b.sport)
      } else { av = 0; bv = 0 }
      return sortDir === 'desc' ? bv - av : av - bv
    })
    // calculateBonusConversion is an inline fn (new identity each render); adding it would defeat the memo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunities, filters, defaultBonusAmount, sortBy, sortDir])

  const visibleOpportunities = useMemo(
    () => filteredOpportunities.filter(opp => !hiddenBets[generateHideId(opp)]),
    [filteredOpportunities, hiddenBets]
  )

  const totalPages = Math.max(1, Math.ceil(visibleOpportunities.length / itemsPerPage))
  useEffect(() => { if (currentPage > totalPages) setCurrentPage(1) }, [totalPages, currentPage])
  useEffect(() => { setCurrentPage(1) }, [filters, sortBy, sortDir])

  const pagedOpps = isPreview
    ? visibleOpportunities.slice(0, 3)
    : visibleOpportunities.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  const hiddenCount = Object.keys(hiddenBets).length

  if (!isVisible) return null

  return (
    <div className="bcm-container">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="bcm-header">
        <div className="bcm-action-row">

          <div className="bcm-action-left">
            <button className="bc-btn" onClick={() => setShowFilterPanel(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
              </svg>
              Filters
            </button>

            {hiddenCount > 0 && (
              <button className="bc-btn" onClick={() => setShowHiddenModal(true)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
                {hiddenCount}
              </button>
            )}
          </div>

          <div className="bcm-action-right">
            <button
              className={`bc-btn bc-btn-toggle${autoRefreshEnabled ? ' toggle-active' : ''}`}
              onClick={() => setAutoRefreshEnabled(v => !v)}
            >
              Auto
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </button>
            <button className="bc-btn bc-icon-btn" onClick={() => fetchOpportunities()} disabled={loading}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={loading ? 'bc-spin' : ''}>
                <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </button>
          </div>

        </div>
      </div>

      {error && <div className="bc-error" style={{ margin: '12px' }}>Error: {error}</div>}
      {loading && <div className="bc-loading"><div className="bc-spinner" /></div>}

      {/* ── Cards ───────────────────────────────────────────────────────── */}
      {!loading && (
        <div className="bcm-cards">
          {pagedOpps.map((opp, idx) => {
            const rowId      = opp._id
            const isExpanded = expandedCard === rowId
            const blurCard   = isPreview && idx > 0
            const blurStyle  = blurCard ? { filter: 'blur(5px)', userSelect: 'none', pointerEvents: 'none' } : {}

            const customOutcomes  = opp.outcomes?.map((o, oi) => ({ ...o, odds: getEditableOdds(rowId, oi, o.odds) }))
            const editedBonusAmt  = getEditableBonusAmt(rowId)
            const calc            = calculateBonusConversion(customOutcomes, editedBonusAmt)

            return (
              <React.Fragment key={rowId}>
                <BonusCard
                  opp={opp}
                  oddsText={opp.outcomes?.map((o, oi) => convertOdds(getEditableOdds(rowId, oi, o.odds), oddsFormat)) || []}
                  bonusStake={calc.bonusStake}
                  hedgeStake={calc.hedgeStake}
                  retention={calc.retentionPercentage}
                  style={blurStyle}
                  blurStats={isPreview && idx === 0}
                  isExpanded={isExpanded}
                  tracked={track.isTracked(opp)}
                  showButtons={!isPreview}
                  onOpenBook={(outcome) => !isPreview && outcome.game_url && window.open(outcome.game_url, '_blank')}
                  onExpand={() => setExpandedCard(isExpanded ? null : rowId)}
                  onTrack={() => !track.isTracked(opp) && track.open(opp, customOutcomes, editedBonusAmt)}
                  onHide={() => hideOpportunity(opp)}
                >
                  {/* ── Expanded calculator ────────────────────────────── */}
                  {isExpanded && (
                    <div className="arb-mob-expanded">

                      {/* Bonus amount bar */}
                      <div className="arb-mob-stake-bar">
                        <span className="arb-mob-stake-label">Bonus Amount</span>
                        <div className="arb-exp-topbar-input-wrap">
                          <span className="arb-exp-topbar-prefix">$</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={getEditableBonusRaw(rowId)}
                            onChange={e => updateBonusAmount(rowId, e.target.value)}
                            className="arb-exp-stake-input"
                          />
                        </div>
                      </div>

                      {/* Outcome cards */}
                      {opp.outcomes?.map((outcome, oi) => {
                        const logo    = BOOKMAKER_LOGOS[outcome.bookmaker]
                        const isBonus = outcome.bet_type === 'BONUS'
                        const stakeDisplay  = isBonus ? calc.bonusStake : calc.hedgeStake
                        const winProfit     = isBonus ? calc.bonusWinProfit : calc.hedgeWinProfit
                        return (
                          <div key={oi} className="arb-mob-outcome-card">
                            <div className="arb-mob-card-header">
                              {logo
                                ? <img src={logo} alt={outcome.bookmaker} className="arb-outcome-card-logo" />
                                : <div className="arb-outcome-card-logo-placeholder">{outcome.bookmaker.slice(0, 3).toUpperCase()}</div>
                              }
                              <div>
                                <div className="arb-outcome-card-name">
                                  {outcome.bookmaker}
                                  <span className={`bonus-leg-label ${isBonus ? 'bonus-leg-label-bonus' : 'bonus-leg-label-hedge'}`} style={{ marginLeft: '6px' }}>
                                    {isBonus ? 'BONUS' : 'HEDGE'}
                                  </span>
                                </div>
                                <div className="arb-outcome-card-sel">{formatSelection(outcome.selection, opp.market_type, opp.player_name)}</div>
                              </div>
                            </div>
                            <div className="arb-mob-card-fields">
                              <div className="arb-mob-field">
                                <span className="arb-outcome-field-label">Odds</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={getEditableOddsRaw(rowId, oi, outcome.odds)}
                                  onChange={e => updateOdds(rowId, oi, e.target.value)}
                                  className="arb-outcome-odds-input"
                                />
                              </div>
                              <div className="arb-mob-field">
                                <span className="arb-outcome-field-label">Stake</span>
                                <span className="arb-outcome-field-val">${stakeDisplay}</span>
                              </div>
                              <div className="arb-mob-field">
                                <span className="arb-outcome-field-label">If wins</span>
                                <span className={typeof winProfit === 'number' && winProfit >= 0 ? 'arb-outcome-field-profit-pos' : 'arb-outcome-field-profit-neg'}>
                                  {typeof winProfit === 'number' ? `$${winProfit}` : '—'}
                                </span>
                              </div>
                            </div>
                            <button className="arb-open-btn" onClick={() => outcome.game_url && window.open(outcome.game_url, '_blank')} disabled={!outcome.game_url}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                                <polyline points="15 3 21 3 21 9"/>
                                <line x1="10" y1="14" x2="21" y2="3"/>
                              </svg>
                              Open {outcome.bookmaker}
                            </button>
                          </div>
                        )
                      })}

                      {/* Guaranteed profit + retention summary */}
                      <div className="arb-mob-stake-bar" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span className="arb-mob-stake-label">Guaranteed Profit</span>
                          <span className="arb-outcome-field-profit-pos" style={{ fontSize: '13px', fontWeight: 700 }}>${calc.guaranteedProfit}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span className="arb-mob-stake-label">Retention</span>
                          <span className="arb-outcome-field-val">{calc.retentionPercentage.toFixed(1)}%</span>
                        </div>
                      </div>

                    </div>
                  )}

                </BonusCard>

              {/* Preview wall — right below the first (teaser) card */}
              {isPreview && idx === 0 && (
                <PreviewWall
              title={visibleOpportunities.length > 0
                ? `${visibleOpportunities.length} bonus conversion opportunities right now`
                : 'Bonus conversion opportunities right now'}
              features={['Convert bonus bets to guaranteed profit', 'Real-time odds from all major bookmakers', 'Automatic hedge stake calculation', 'Much more...']}
              planName="Promo Premium"
              accent="#a78bfa"
              hadTrial={hadTrial}
            />
              )}
              </React.Fragment>
            )
          })}

          {/* Preview wall fallback — still shows when there are zero opportunities */}
          {isPreview && pagedOpps.length === 0 && (
            <PreviewWall
              title={visibleOpportunities.length > 0
                ? `${visibleOpportunities.length} bonus conversion opportunities right now`
                : 'Bonus conversion opportunities right now'}
              features={['Convert bonus bets to guaranteed profit', 'Real-time odds from all major bookmakers', 'Automatic hedge stake calculation', 'Much more...']}
              planName="Promo Premium"
              accent="#a78bfa"
              hadTrial={hadTrial}
            />
          )}

          {!isPreview && visibleOpportunities.length === 0 && (
            <div className="bc-empty">No bonus opportunities match your filters.</div>
          )}
        </div>
      )}

      {/* ── Pagination ──────────────────────────────────────────────────── */}
      {!isPreview && !loading && totalPages > 1 && (
        <div className="bcm-pagination">
          <button className="bc-page-arrow" onClick={() => { setCurrentPage(1); window.scrollTo({ top: 0 }) }} disabled={currentPage === 1}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="11 18 5 12 11 6"/><polyline points="19 18 13 12 19 6"/></svg>
          </button>
          <button className="bc-page-arrow" onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0 }) }} disabled={currentPage === 1}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <span className="bc-page-info">{currentPage} / {totalPages}</span>
          <button className="bc-page-arrow" onClick={() => { setCurrentPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0 }) }} disabled={currentPage === totalPages}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <button className="bc-page-arrow" onClick={() => { setCurrentPage(totalPages); window.scrollTo({ top: 0 }) }} disabled={currentPage === totalPages}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="5 18 11 12 5 6"/><polyline points="13 18 19 12 13 6"/></svg>
          </button>
        </div>
      )}

      {/* ── Modals ──────────────────────────────────────────────────────── */}
      <FilterPanel
        isOpen={showFilterPanel}
        onClose={() => setShowFilterPanel(false)}
        filters={filters}
        setFilters={setFilters}
        bonusAmount={defaultBonusAmount}
        setBonusAmount={setDefaultBonusAmount}
        savedFilters={savedFilters}
        activeFilterId={activeFilterId}
        filterName={filterName}
        setFilterName={setFilterName}
        onSaveFilter={handleSaveFilter}
        onLoadFilter={handleLoadFilter}
        onDeleteFilter={handleDeleteFilter}
        onToggleDefault={handleToggleDefault}
        onReset={resetFilters}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortDir={sortDir}
        setSortDir={setSortDir}
      />

      <HiddenBetsModal
        isOpen={showHiddenModal}
        hiddenBets={hiddenBets}
        onUnhide={unhideOpportunity}
        onUnhideAll={clearAllHidden}
        onClose={() => setShowHiddenModal(false)}
      />

      <TrackBetModal
        opp={track.opp}
        form={track.form}
        onFormChange={track.setForm}
        onConfirm={() => track.save(false)}
        onConfirmAndHide={() => track.save(true)}
        onClose={track.close}
        formatSelection={formatSelection}
      />

      <BonusTour isVisible={isVisible} isMobile />

    </div>
  )
}
